import '#lit/components/base/audience_builder_field/index.js';

import {customElement, property, state} from 'lit/decorators.js';
import styles from './style.scss';
import type {Option} from '#lit/components/base/select_async/index.js';
import SelectAsync from '#lit/components/base/select_async/index.js';
import {consume} from '@lit-labs/context';
import {RequestMethod} from '#lit/common/constants/index.js';
import type {
  FilterSearchResponse,
  FilterSearchResultItem,
} from '#lit/components/audience_builder/filter_addition_view/index.js';
import {DEFAULT_SEARCH_LIMIT} from '#lit/components/audience_builder/filter_addition_view/index.js';
import type {EntryData} from '#lit/contexts/entry_data.js';
import {
  DEFAULT_ENTRY_DATA,
  EntryDataContext,
} from '#lit/contexts/entry_data.js';
import type {Dataset} from '#lit/types/datasets.js';
import {DatasetType} from '#lit/types/datasets.js';
import type {DestinationConnection} from '#types/destinations.js';
import {isAbortError, FlyErrorCritical} from '#lit/utils/errors.js';
import {fetcher} from '#lit/utils/http.js';
import {displayApiError} from '#lit/utils/notifications.js';
import {journeyRequestCache} from '#lit/common/constants/journeys.js';
import {AskForPii, searchFieldsUrl} from '#lit/utils/journeys.js';
import type {AudienceBuilderField} from '#lit/types/builders/audience_builder/index.js';
import {ExpressionTypes} from '#lit/types/builders/audience_builder/index.js';
import type {AudienceDatasetIds} from '#lit/contexts/audience.js';
import {AudienceContext} from '#lit/contexts/audience.js';
import type {Nullable} from '#lit/types/general.js';
import type {TemplateResult} from 'lit';
import {html} from 'lit';

export const MATCH_FIELD_SELECT_ELEMENT_TAG = 'match-field-select';

@customElement(MATCH_FIELD_SELECT_ELEMENT_TAG)
export default class MatchFieldSelect extends SelectAsync {
  @consume({context: EntryDataContext, subscribe: true})
  @property({type: Object})
  entryData: EntryData = DEFAULT_ENTRY_DATA;

  @consume({context: AudienceContext, subscribe: true})
  @property({type: Object})
  audience: Nullable<AudienceDatasetIds> = null;

  @property({type: Object})
  selectedDataset!: Dataset;

  @property({type: Boolean})
  disabled = false;

  @property({type: Object})
  destination!: DestinationConnection;

  @property()
  datasetType: DatasetType = DatasetType.USER;

  @property({type: String})
  placeholder = 'Select values';

  @property({type: Array})
  compatibleTypes: string[] = [];

  @state()
  limit = DEFAULT_SEARCH_LIMIT;

  @property({type: Boolean})
  autofocusSearch = false;

  @property({type: Number})
  queueEventID?: number;

  static get styles() {
    return [super.styles, styles];
  }

  valueSearchURL(): string {
    if (!state) throw Error('Invalid query state.');
    const queryState = this.queryStates.get(this.query);
    return searchFieldsUrl({
      datasetGroupId:
        this.entryData.datasetGroup?.id ?? this.audience?.dataset_group_id,
      limit: this.limit,
      query: this.query,
      datasetId: this.selectedDataset?.id ?? this.audience?.dataset_id,
      page: queryState?.page,
      includeSystemAndComputedFields: true,
      pii: AskForPii.YES,
      queueEventID: this.queueEventID,
    });
  }

  async fetchHandler(_: AbortSignal): Promise<void> {
    const url = this.valueSearchURL();

    try {
      const queryState = this.queryStates.get(this.query);
      if (!queryState) throw Error('Invalid query.');

      const response = journeyRequestCache.has(url)
        ? (journeyRequestCache.get(url) as FilterSearchResponse)
        : await fetcher<FilterSearchResponse>(url, {
            method: RequestMethod.GET,
          });

      queryState.page += 1;

      const allowedTypes = new Set(this.compatibleTypes);

      let options: Option<AudienceBuilderField>[] = (
        response!.results as FilterSearchResultItem[]
      ).map(result => {
        const type = this.queueEventID ? ExpressionTypes.REALTIME : result.type;
        const option = {
          value: result.id,
          label: result.display_name || result.name,
          selected: this.selectedValue === result.id,
          record: {
            ...result,
            type,
          },
        };

        if (!this.options.has(option.value)) {
          this.options.set(option.value, option);
        }

        if (option.selected) {
          this.options.set(option.value, option);
        }

        return option;
      });

      if (this.compatibleTypes.length > 0) {
        options = options.filter(option =>
          allowedTypes.has(option.record.type)
        );
      }

      queryState.more = response!.more as boolean;
      queryState.results = new Set([
        ...queryState.results,
        ...options.map(o => o.value),
      ]);
      if (!journeyRequestCache.has(url)) {
        journeyRequestCache.set(url, response!);
      }
    } catch (err) {
      console.error(err);
      if (isAbortError(err)) return;

      new FlyErrorCritical(
        `Error fetching field values for:${this.selectedDataset.id}`,
        {
          label: 'request',
          data: `GET: ${url}`,
        },
        {
          label: 'response',
          data: err,
        }
      ).register('Error in expression multiselect');

      displayApiError(err);
    }
  }

  onOptionClick(_: Option): void {}

  renderOption(option: Option<AudienceBuilderField>): TemplateResult {
    return html`
      <fly-list-item
        ?selected=${this.selectedValue === option.value}
        @selected=${() => {
          this.selectOption(option);
        }}
        @deselected=${() => {
          if (option.value === this.selectedValue) {
            this.deselectOption(option);
          }
        }}
      >
        <audience-builder-field .option=${option}> </audience-builder-field>
      </fly-list-item>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [MATCH_FIELD_SELECT_ELEMENT_TAG]: MatchFieldSelect;
  }
}
